import React, { useState, useEffect } from 'react';
import { getOverlayPrefs, setOverlayPref, subscribeOverlayPrefs } from '../common/overlayPrefs';
import { hasWebCodecs } from '../util/detectWebSocketSupport';

/**
 * CameraOverlaysContent — the shared body of the "Camera overlays" control: the overlay toggle
 * rows (Camera Info / Object Detection / License Plates) + the two grid actions (Snapshot all
 * tiles / Reset digital zoom). ONE source so the desktop bottom-bar gear popover
 * (CameraOverlaysMenu) and the mobile/tablet bottom sheet (CameraOverlaysSheet) can never drift
 * apart (#86bb5dx62). Self-manages the shared overlayPrefs store, so both chromes reflect live
 * changes; each wrapper owns only its own open/close chrome.
 *
 * "Camera Info" here = the FPS/bitrate/codec/resolution readout (overlayPrefs.metadata, Java
 * parity); the name+time overlay is "Name & Timestamp" (overlayPrefs.info, Setup > Display).
 *
 * Props: onSnapshotAll(), onResetZoom() — the wrappers pass close-then-act handlers.
 */
const Switch = ({ on }) => <span className={'live-ovsw' + (on ? ' live-ovsw--on' : '')} aria-hidden="true" />;

const CameraOverlaysContent = ({ onResetZoom }) => {   // onSnapshotAll prop retired — Snapshot-all benched (#86baz05kd)
    const [prefs, setPrefs] = useState(getOverlayPrefs);
    useEffect(() => subscribeOverlayPrefs(setPrefs), []);
    const toggle = (key) => setOverlayPref(key, !prefs[key]);
    // v1 coarse gate: the ws-canvas overlays (Object Detection, License Plates) can only render
    // over a WS/WebCodecs stream (secure context). Without WebCodecs they are dead switches, so
    // hide them; Camera Info + Name & Timestamp stay (they also work on the JPEG path). Server-side
    // per-NVR / per-eview-member + OD/LPR-capability gating is a v2 refinement.
    const canvasOverlays = hasWebCodecs();

    return (
        <>
            <div className="live-ovmenu__row" onClick={() => toggle('metadata')} role="menuitemcheckbox" aria-checked={prefs.metadata}>
                <div>Camera Info<small>FPS &middot; bitrate &middot; codec &middot; resolution</small></div><Switch on={prefs.metadata} />
            </div>
            {canvasOverlays && (
                <div className="live-ovmenu__row" onClick={() => toggle('objects')} role="menuitemcheckbox" aria-checked={prefs.objects}>
                    Object Detection<Switch on={prefs.objects} />
                </div>
            )}
            {canvasOverlays && (
                <div className="live-ovmenu__row" onClick={() => toggle('lpr')} role="menuitemcheckbox" aria-checked={prefs.lpr}>
                    License Plates<Switch on={prefs.lpr} />
                </div>
            )}
            <div className="live-ovmenu__div" />
            {/* "Snapshot all tiles" BENCHED 2026-08-16 (#86baz05kd): the composite cannot capture EVERY
                camera — a cross-origin eView member frame can't be read into the canvas (mpengine sends CORS
                only on the OPTIONS preflight, not the GET response), so it would silently drop those cams.
                A partial grid is unacceptable for documentation/evidence. Revive once mpengine sends
                Access-Control-Allow-Origin on its GET — docs/java-parity-audit/SESSION-STATUS-composite-and-full-verify.md. */}
            <div className="live-ovmenu__act" onClick={onResetZoom} role="menuitem">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 15H3v4c0 1.1.9 2 2 2h4v-2H5v-4zM5 5h4V3H5c-1.1 0-2 .9-2 2v4h2V5zm14-2h-4v2h4v4h2V5c0-1.1-.9-2-2-2zm0 16h-4v2h4c1.1 0 2-.9 2-2v-4h-2v4z" /></svg>Reset digital zoom
            </div>
        </>
    );
};

export default CameraOverlaysContent;
